import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppScope } from './app_scope.service';

/**
 * Configuration structure for account-specific themes.
 * Each account can have its own color scheme and logo.
 */
export interface ThemeConfig {
  theme: string;  // Theme identifier (e.g., 'classic_wire', 'crimson_alloy')
  theme_config: {
    name: string;  // Display name for the theme
    header_color: string;  // Hex color for the main header bar
    time_traveler_color: string;  // Hex color for the time traveler/date picker bar
    description: string;  // User-friendly description
  };
  right_logo: string | null;  // Optional custom logo filename
}

/**
 * Service responsible for applying dynamic themes to the WIRE application.
 * Automatically adjusts colors for contrast and readability based on luminance.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly appScope = inject(AppScope);

  // Observable stream of theme configuration changes
  private themeConfigSubject = new BehaviorSubject<ThemeConfig | null>(null);
  public themeConfig$ = this.themeConfigSubject.asObservable();

  // Observable stream of logo URL changes
  private rightLogoUrlSubject = new BehaviorSubject<string>('/admin/myfiles/download/right-logo.png');
  public rightLogoUrl$ = this.rightLogoUrlSubject.asObservable();

  constructor() {
    // Listen for configuration changes from the backend
    // When account switches or theme is updated, this will trigger
    this.appScope.configDictChanged.subscribe(configMap => {
      this.loadThemeConfig(configMap);
    });
  }

  /**
   * Loads theme configuration from the backend config map.
   * Updates observables and applies the theme to the DOM.
   */
  private loadThemeConfig(configMap: any): void {
    if (configMap && configMap['account_theme']) {
      const themeConfig = configMap['account_theme'].value as ThemeConfig;
      this.themeConfigSubject.next(themeConfig);

      if (themeConfig.right_logo) {
        this.rightLogoUrlSubject.next(`/admin/myfiles/download/${themeConfig.right_logo}`);
      } else {
        this.rightLogoUrlSubject.next('/admin/myfiles/download/right-logo.png');
      }

      // Apply CSS variables and classes for the theme
      this.applyDynamicTheme(themeConfig);
    }
  }

  /**
   * Applies the theme by setting CSS variables on the document root.
   * Automatically calculates appropriate text/icon colors based on background luminance.
   */
  private applyDynamicTheme(config: ThemeConfig): void {
    if (!config || !config.theme_config) {
      return;
    }

    // Classic theme uses the original SCSS styles without dynamic overrides
    if (config.theme === 'classic_wire') {
      this.clearDynamicTheme();
      return;
    }

    const root = document.documentElement;
    const headerColor = config.theme_config.header_color;
    const travelerColor = config.theme_config.time_traveler_color;

    // Set primary background colors
    root.style.setProperty('--wire-header-bg', headerColor);
    root.style.setProperty('--wire-traveler-bg', travelerColor);

    // Calculate luminance to determine if backgrounds are light or dark
    // Luminance < 0.5 = dark background, > 0.5 = light background
    const headerLuminance = this.getLuminance(headerColor);
    const travelerLuminance = this.getLuminance(travelerColor);

    // Set text colors for optimal contrast
    // Dark backgrounds get white text, light backgrounds get dark text
    const isDarkHeader = headerLuminance < 0.5;
    const isDarkTraveler = travelerLuminance < 0.5;

    root.style.setProperty('--wire-header-text', isDarkHeader ? '#ffffff' : '#1a1a1c');
    root.style.setProperty('--wire-header-text-secondary',
      isDarkHeader ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)');

    root.style.setProperty('--wire-traveler-text', isDarkTraveler ? '#ffffff' : '#1a1a1c');
    root.style.setProperty('--wire-traveler-text-secondary',
      isDarkTraveler ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)');

    // Create hover states: lighten dark backgrounds, darken light backgrounds
    root.style.setProperty('--wire-header-hover',
      this.adjustBrightness(headerColor, isDarkHeader ? 0.1 : -0.1));
    root.style.setProperty('--wire-traveler-hover',
      this.adjustBrightness(travelerColor, isDarkTraveler ? 0.1 : -0.1));

    // Create a gradient based on header color for the date-period border
    // This replaces the orange gradient with a theme-appropriate one
    const gradientDark = this.adjustBrightness(headerColor, -0.15);
    const gradientMedium = this.adjustBrightness(headerColor, -0.1);
    const gradientLight = this.adjustBrightness(headerColor, -0.05);
    root.style.setProperty('--wire-date-period-gradient',
      `linear-gradient(90deg, ${gradientLight} 0%, ${gradientMedium} 25%, ${gradientDark} 50%, ${gradientMedium} 85%, ${gradientLight} 100%)`);

    // Input/button backgrounds: semi-transparent overlays
    root.style.setProperty('--wire-header-input-bg',
      isDarkHeader ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)');
    root.style.setProperty('--wire-traveler-input-bg',
      isDarkTraveler ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)');
    root.style.setProperty('--wire-traveler-input-hover',
      isDarkTraveler ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)');

    // Border colors for form elements
    root.style.setProperty('--wire-traveler-border',
      isDarkTraveler ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)');
    root.style.setProperty('--wire-traveler-border-focus',
      isDarkTraveler ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)');

    // Icon filters for SVG/image icons that can't be colored with CSS
    // Comment icon is light gray by default, invert on light backgrounds
    root.style.setProperty('--wire-header-icon-filter', isDarkHeader ? 'none' : 'invert(1)');

    // Calendar/dropdown icons are dark by default, invert only on very dark backgrounds
    const needsInvertedIcons = travelerLuminance < 0.2;
    root.style.setProperty('--wire-calendar-icon-filter', needsInvertedIcons ? 'invert(1)' : 'none');
    root.style.setProperty('--wire-dropdown-arrow-filter', needsInvertedIcons ? 'invert(1)' : 'none');

    // Add class to body to activate theme-specific CSS rules
    document.body.classList.add('wire-theme-dynamic');
  }

  /**
   * Removes all dynamic theme styles and reverts to classic theme.
   */
  private clearDynamicTheme(): void {
    document.body.classList.remove('wire-theme-dynamic');
    const root = document.documentElement;
    const properties = [
      '--wire-header-bg', '--wire-traveler-bg',
      '--wire-header-text', '--wire-header-text-secondary',
      '--wire-traveler-text', '--wire-traveler-text-secondary',
      '--wire-header-hover', '--wire-traveler-hover',
      '--wire-header-input-bg', '--wire-traveler-input-bg',
      '--wire-traveler-input-hover', '--wire-traveler-border',
      '--wire-traveler-border-focus', '--wire-date-period-gradient',
      '--wire-header-icon-filter', '--wire-calendar-icon-filter',
      '--wire-dropdown-arrow-filter'
    ];
    properties.forEach(prop => root.style.removeProperty(prop));
  }

  /**
   * Calculates the relative luminance of a color using the WCAG formula.
   * Returns a value between 0 (black) and 1 (white).
   * Used to determine if text should be light or dark for contrast.
   * @see https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
   */
  private getLuminance(hex: string): number {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return 0.5; // Default to middle if parsing fails

    // Normalize RGB values to 0-1 range
    const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255];

    // Apply gamma correction (sRGB to linear RGB)
    const linearize = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

    // Calculate luminance using ITU-R BT.709 coefficients
    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
  }

  /**
   * Converts a hex color string to RGB values.
   * Handles both #RRGGBB and RRGGBB formats.
   */
  private hexToRgb(hex: string): {r: number, g: number, b: number} | null {
    hex = hex.replace('#', '').toLowerCase();

    // Match 6 hex digits (RRGGBB)
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Adjusts the brightness of a color by a given amount.
   * @param hex - The hex color to adjust
   * @param amount - Amount to adjust (-1 to 1, negative darkens, positive lightens)
   */
  private adjustBrightness(hex: string, amount: number): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;

    // Adjust each channel and clamp to 0-255
    const adjust = (value: number) => Math.round(Math.max(0, Math.min(255, value + (amount * 255))));
    const adjusted = {
      r: adjust(rgb.r),
      g: adjust(rgb.g),
      b: adjust(rgb.b)
    };

    // Convert back to hex with leading zeros
    const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
    return `#${toHex(adjusted.r)}${toHex(adjusted.g)}${toHex(adjusted.b)}`;
  }

  /**
   * Gets the current theme configuration.
   */
  public getThemeConfig(): ThemeConfig | null {
    return this.themeConfigSubject.value;
  }

  /**
   * Gets the current logo URL.
   */
  public getRightLogoUrl(): string {
    return this.rightLogoUrlSubject.value;
  }
}
